import type { Translate } from 'next-translate';
import Trans from 'next-translate/Trans';
import Link from 'next/link';
import React from 'react';
import { type DropzoneOptions } from 'react-dropzone';
import { Container } from '@/components/elements/Container';
import { MyImage } from '@/components/elements/MyImage';
import { VideoLoadingOverlay } from '@/components/elements/VideoLoadingOverlay';
import { Dropzone } from '@/components/landing/reusableSections';
import { getAcceptedVideoTypes, niceBytes } from '@/helpers';
import { type HeroWithVideoUpload } from '@/types';
import { LANDING_CONSTANTS } from '@/constants';
import bgMobile from '@/public/images/landing/backgrounds/bg-abstract-mobile.webp';
import bg from '@/public/images/landing/backgrounds/bg-abstract.webp';
import styles from './HeroVideoUpload.module.scss';

interface Props {
	data: HeroWithVideoUpload;
	t: Translate;
	dropzoneOptions: DropzoneOptions;
	isLoading: boolean;
	uploadPercentage?: number;
	/** Photo uploads report no progress, so they use the button spinner instead */
	withLoadingOverlay?: boolean;
}

export const HeroVideoUpload: React.FC<Props> = ({
	data,
	t,
	dropzoneOptions,
	isLoading,
	uploadPercentage = 0,
	withLoadingOverlay = true,
}) => {
	const { title, description, buttonText, data: info } = data;

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const normalizeMaxSize = niceBytes({
		bytes: dropzoneOptions.maxSize ?? LANDING_CONSTANTS.MAX_VIDEO_FILE_SIZE,
	});

	const formatsLabel = info.formatsLabel ? t(info.formatsLabel) : undefined;

	const maxSizeLabel = info.maxSizeLabel
		? t(info.maxSizeLabel, {
				photoSize: niceBytes({
					bytes: LANDING_CONSTANTS.MAX_FILE_SIZE,
					decimalNumber: 0,
				}),
				videoSize: niceBytes({
					bytes: LANDING_CONSTANTS.MAX_VIDEO_FILE_SIZE,
					decimalNumber: 0,
				}),
		  })
		: undefined;

	const normalizeTypes =
		formatsLabel ??
		getAcceptedVideoTypes(
			Object.values(
				dropzoneOptions.accept ??
					LANDING_CONSTANTS.ACCEPTED_VIDEO_TYPES_DROPZONE,
			).flat(),
		);

	return (
		<section className={`${styles['hero']}`}>
			<MyImage
				imageProps={{
					src: bgMobile,
					alt: 'background image',
					title: `${t(title)}`,
					priority: true,
					fetchPriority: 'low',
				}}
				settings={{
					classes: {
						picture: `${styles['hero__background']}`,
						image: styles['hero__background-img'],
					},
				}}
			>
				<source srcSet={bg.src} media='(min-width: 1023px)' />
			</MyImage>
			<Container className={`${styles['hero__container']}`}>
				<div className={`${styles['hero__info']}`}>
					<h1 className={`${styles['hero__title']}`}>{t(title)}</h1>
					<p className={`${styles['hero__subtitle']}`}>{t(description)}</p>
				</div>

				<div className={`${styles['hero__dropzone-wrapper']}`}>
					<Dropzone
						isLoading={isLoading}
						title={t(info.dropzoneTitle)}
						buttonText={t(buttonText)}
						options={dropzoneOptions}
						classes={{
							dropzone: styles['hero__dropzone'],
							info: styles['hero__dropzone-info'],
						}}
						onErrorMessageChange={setErrorMessage}
						video
						maxSizeText={maxSizeLabel}
						typesText={formatsLabel}
					/>

					<div className={styles['hero__dropzone-mobile-info']}>
						{errorMessage && (
							<span
								className={`${styles['hero__dropzone-mobile-text']} ${styles['hero__dropzone-mobile-text_error']}`}
							>
								{errorMessage}
							</span>
						)}
						<div className={styles['hero__dropzone-mobile-info-container']}>
							<span className={styles['hero__dropzone-mobile-text']}>
								{normalizeTypes}
							</span>
							<span className={styles['hero__dropzone-mobile-text']}>
								Max - {maxSizeLabel ?? normalizeMaxSize}
							</span>
						</div>
					</div>

					<span className={`${styles['hero__agree']}`}>
						<Trans
							i18nKey='common:agreeUploadFile'
							components={{
								terms: <Link href='/terms-of-use' target='_blank' />,
								privacy: <Link href='/privacy-policy' target='_blank' />,
							}}
						/>
					</span>
				</div>
				{isLoading && withLoadingOverlay && (
					<VideoLoadingOverlay percentage={uploadPercentage} />
				)}
			</Container>
		</section>
	);
};
